import React, { useEffect, useState } from 'react';

import { Row, Col, Input, Card, CardBody } from 'reactstrap';
import { api } from '../../config';
import ActionButton from '../../Components/Common/ActionButton';
import { EmptyState } from '../../Components/Common/ListStates';
import { SkeletonCard } from '../../Components/Common/Skeleton';
import { formatDate } from '../../utils/format';

const SupportDocumentTab = ({
  ids,
  token,
  setIsLoading: _setIsLoading,
  isLoading: _isLoading,
}) => {
  const [uploadDocument, setUploadDocument] = useState({ items: [] });
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (ids) {
      fetchDocumentUpload(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const mapSupportDocumentItem = item => ({
    id: item.id ?? null,
    customFileName: item.customFileName
      ? item.customFileName.replace(/\.[^/.]+$/, '')
      : null,
    documentName: item.documentUpload?.documentName ?? null,
    documentUploadId: item.documentUploadId ?? null,
    actualUrl: item.link ?? null,
    isMasterDocument: !item.link,
    isShowToDMUT: item.isShowToDMUT ?? false,
    lastUpdated: item.updatedDate ? formatDate(item.updatedDate) : null,
    createdDate: item.createdDate ? formatDate(item.createdDate) : null,
  });

  const fetchDocumentUpload = async ids => {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${ids}`,
        {
          params: { ProcurementId: ids },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = (result.data?.items ?? [])
        .map(mapSupportDocumentItem)
        .filter(item => item.isShowToDMUT === true);
      setUploadDocument({ items });
    } catch {
      void 0;
    } finally {
      setDocsLoaded(true);
    }
  };

  const handleDirectOpenNewTab = url => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank'; // buka tab baru
    link.rel = 'noopener noreferrer'; // best practice
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const term = searchText.trim().toLowerCase();
  const filteredDocuments = uploadDocument.items.filter(
    doc =>
      !term ||
      [doc.documentName, doc.customFileName].some(value =>
        (value || '').toLowerCase().includes(term)
      )
  );

  return (
    <React.Fragment>
      <Row>
        <div>
          <Row>
            <Col sm={12}>
              <div color='dark' className='d-flex align-items-center mb-2  '>
                <div className='flex-grow-1'>
                  <h5 className='mb-1'>Document Information</h5>

                  <p className='text-muted mb-2 fs-12 '>
                    Bisa jadi Kalimat tambahan untuk membantu section mengenai
                    apa ini
                  </p>
                  <hr className='text-muted opacity-25' />
                  <div className='d-flex flex-wrap align-items-center gap-2 mb-4'>
                    <div
                      className='position-relative'
                      style={{ width: '250px', maxWidth: '100%' }}
                    >
                      <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-2'></i>
                      <Input
                        type='text'
                        placeholder='Search...'
                        className='ps-5'
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                      />
                    </div>
                    <ActionButton
                      variant='tertiary'
                      icon='ri-filter-off-line'
                      onClick={() => setSearchText('')}
                    >
                      Clear Filter
                    </ActionButton>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row>
            <Col sm={12}>
              <h6 className='fw-medium text-dark mb-3'>
                Selected/Uploaded Document
              </h6>

              {!docsLoaded ? (
                <>
                  <SkeletonCard lines={1} className='border mb-3' />
                  <SkeletonCard lines={1} className='border mb-3' />
                </>
              ) : filteredDocuments.length === 0 ? (
                <EmptyState
                  compact
                  icon='ri-file-text-line'
                  title='No documents shared'
                  emptyHint='No supporting documents have been shared for this tender invitation yet.'
                  hasFilters={Boolean(term)}
                  onClear={() => setSearchText('')}
                />
              ) : (
                <div className='d-flex flex-column gap-2'>
                  {filteredDocuments.map((data, _index) => (
                    <Card key={data.id} className='border shadow-none mb-0'>
                      <CardBody className='p-3'>
                        <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3'>
                          {/* Left Content */}
                          <div className='d-flex flex-grow-1 min-w-0 gap-3'>
                            <div
                              className={`flex-shrink-0 p-2 rounded ${
                                data.isMasterDocument
                                  ? 'bg-success-subtle text-success'
                                  : 'bg-primary-subtle text-primary'
                              }`}
                            >
                              <i
                                className={`fs-20 ${
                                  data.isMasterDocument
                                    ? 'ri-file-text-line text-success'
                                    : 'ri-file-text-line text-primary'
                                }`}
                              />
                            </div>

                            <div className='flex-grow-1 min-w-0'>
                              {/* Title */}
                              <div className='fw-medium text-dark mb-1'>
                                {data.documentName ||
                                  data.customFileName ||
                                  '-'}
                              </div>

                              {/* Meta */}
                              <div className='d-flex flex-wrap align-items-center gap-2'>
                                <span
                                  className={`badge ${
                                    data.isMasterDocument
                                      ? 'bg-success-subtle text-success'
                                      : 'bg-primary-subtle text-primary'
                                  }`}
                                >
                                  {data.isMasterDocument
                                    ? 'Master Document'
                                    : 'Upload Custom'}
                                </span>

                                {(data.lastUpdated || data.createdDate) && (
                                  <span className='text-muted small'>
                                    {data.lastUpdated
                                      ? `Updated: ${data.lastUpdated}`
                                      : `Created: ${data.createdDate}`}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className='d-flex gap-2 flex-shrink-0'>
                            <ActionButton
                              variant='secondary'
                              onClick={() =>
                                handleDirectOpenNewTab(data.actualUrl)
                              }
                            >
                              Download
                              <i
                                className='ri-download-2-line ms-2'
                                aria-hidden='true'
                              />
                            </ActionButton>

                            <ActionButton
                              variant='secondary'
                              icon='ri-eye-line'
                              onClick={() =>
                                handleDirectOpenNewTab(data.actualUrl)
                              }
                            >
                              View
                            </ActionButton>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Row>
    </React.Fragment>
  );
};

export default SupportDocumentTab;
